import { UpsParty, UpsShipment, UpsShipmentInput, UpsShipmentParty } from '../models/ups-shipment.model';

/**
 * Map a clean UpsShipmentInput into UPS's actual ShipmentRequest JSON. This
 * centralizes the field names/quirks we validated against CIE — `Packaging` (not
 * PackagingType), `Package` as an array, and a `LabelStockSize` required for the
 * ZPL (thermal) label — so callers never have to know them.
 *
 * Returns a ready-to-POST body for the ups-create-shipment route, which passes it
 * straight through to UPS.
 */
export function buildUpsShipmentRequest(input: UpsShipmentInput) {
    const shipper = partyToUps(input.shipper);
    const shipFrom = partyToUps(input.shipFrom ?? input.shipper);
    const shipTo = partyToUps(input.shipTo);

    // UPS marks residential by the mere presence of this (empty-string) indicator.
    if (input.residential) {
        (shipTo.Address as Record<string, unknown>)['ResidentialAddressIndicator'] = '';
    }

    const Package = input.packages.map((pkg) => {
        const p: Record<string, unknown> = {
            Packaging: { Code: '02' }, // 02 = customer-supplied package
            PackageWeight: { UnitOfMeasurement: { Code: 'LBS' }, Weight: String(pkg.weightLbs) },
        };
        if (pkg.lengthIn && pkg.widthIn && pkg.heightIn) {
            p['Dimensions'] = {
                UnitOfMeasurement: { Code: 'IN' },
                Length: String(pkg.lengthIn),
                Width: String(pkg.widthIn),
                Height: String(pkg.heightIn),
            };
        }
        // Declared (insured) value — UPS insures the package and bills the surcharge on the label.
        const declared = Number(pkg.declaredValue);
        if (declared > 0) {
            p['PackageServiceOptions'] = { DeclaredValue: { CurrencyCode: 'USD', MonetaryValue: String(declared) } };
        }
        // Per-package reference numbers — UPS carries these per box, so each package prints its
        // own PO/secondary code on its label (not just the first box's).
        const pkgRefs = (pkg.referenceNumbers ?? []).map(r => (r ?? '').trim()).filter(Boolean).slice(0, 2);
        if (pkgRefs.length) {
            p['ReferenceNumber'] = pkgRefs.map((value, i) => ({ Code: i === 0 ? 'PO' : 'IK', Value: value }));
        }
        return p;
    });

    // Bill-to: shipper (default), receiver, or third party. Receiver/third party
    // bill to a different UPS account number.
    const billing = input.billing ?? { type: 'shipper' as const };
    const shipmentCharge: Record<string, unknown> = { Type: '01' }; // 01 = transportation
    if (billing.type === 'receiver') {
        shipmentCharge['BillReceiver'] = {
            AccountNumber: billing.accountNumber,
            // UPS validates the receiver account against its postal code, so send it when present.
            ...(billing.postalCode
                ? { Address: { PostalCode: billing.postalCode, CountryCode: billing.countryCode || 'US' } }
                : {}),
        };
    } else if (billing.type === 'thirdParty') {
        shipmentCharge['BillThirdParty'] = {
            AccountNumber: billing.accountNumber,
            Address: {
                PostalCode: billing.postalCode ?? '',
                CountryCode: billing.countryCode || 'US',
            },
        };
    } else {
        shipmentCharge['BillShipper'] = { AccountNumber: input.shipper.shipperNumber };
    }

    // Shipment-level reference fallback: if no per-package references were supplied but the
    // shipment carries some (legacy callers), put them on the first package (PO + secondary code).
    if (input.referenceNumbers?.length && Package.length > 0 && !Package[0]['ReferenceNumber']) {
        Package[0]['ReferenceNumber'] = input.referenceNumbers.slice(0, 2).map((value, i) => ({
            Code: i === 0 ? 'PO' : 'IK',
            Value: value,
        }));
    }

    // Quantum View Notify: one Notification entry per (recipient × enabled event).
    // Codes: 6 = ship, 7 = exception, 8 = delivery.
    const Notification: Record<string, unknown>[] = [];
    for (const r of input.notifyRecipients ?? []) {
        const email = (r.email ?? '').trim();
        if (!email) continue;
        const codes = [r.onShip && '6', r.onException && '7', r.onDelivery && '8'].filter(Boolean) as string[];
        for (const code of codes) {
            Notification.push({
                NotificationCode: code,
                EMail: { EMailAddress: email },
            });
        }
    }
    const ShipmentServiceOptions = Notification.length ? { Notification } : undefined;

    return {
        ShipmentRequest: {
            // SubVersion 1707+ so the response carries the ItemizedCharges breakdown.
            Request: { SubVersion: '1707' },
            Shipment: {
                Description: input.description || 'Shipment',
                Shipper: shipper,
                ShipFrom: shipFrom,
                ShipTo: shipTo,
                PaymentInformation: { ShipmentCharge: shipmentCharge },
                Service: { Code: input.serviceCode, Description: input.serviceDescription || '' },
                Package,
                // Return the negotiated + itemized charges that the label is actually billed at,
                // so the saved shipment stores UPS's authoritative breakdown (not just the quote).
                ShipmentRatingOptions: { NegotiatedRatesIndicator: 'Y', RateChartIndicator: 'Y' },
                ...(ShipmentServiceOptions ? { ShipmentServiceOptions } : {}),
            },
            LabelSpecification: {
                LabelImageFormat: { Code: 'ZPL' },
                // Required for thermal (ZPL) labels — 4x6 thermal stock.
                LabelStockSize: { Height: '6', Width: '4' },
            },
        },
    };
}

/**
 * Map a PERSISTED UpsShipment (a saved Draft) into the clean UpsShipmentInput that
 * buildUpsShipmentRequest() consumes — the entity-based counterpart to the builder's form-based
 * buildInput(). Used to process a stored draft (e.g. when a Ground plastics shipment is released)
 * WITHOUT the live builder form. Mirrors buildInput()'s choices: Shipper == the persisted ship-from
 * (UPS defaults ShipFrom to Shipper, so no separate ShipFrom is sent), the ship-to contact is left
 * off the label, and a `receiver` bill-to uses the ship-to's own postal/country (UPS requires it).
 *
 * Pass `resolved` to build from the shipment's REFERENCED address/contact documents (assembled by
 * resolveUpsParty) rather than the snapshot stored on the shipment — that's the id-based path, and
 * the snapshot is only the fallback for records written before ids existed.
 */
export function upsShipmentToInput(
    shipment: UpsShipment,
    resolved?: { shipFrom?: UpsShipmentParty; shipTo?: UpsShipmentParty },
): UpsShipmentInput {
    const trim = (v: unknown): string => (v === null || v === undefined ? '' : String(v)).trim();
    const from = resolved?.shipFrom ?? shipment.shipFrom;
    const to = resolved?.shipTo ?? shipment.shipTo;

    const shipper: UpsParty = {
        name: from?.company ?? '',
        shipperNumber: shipment.shipperNumber,
        attentionName: from?.contactName || undefined,
        phone: from?.phone || undefined,
        address: {
            addressLines: [from?.address?.line1, from?.address?.line2].filter((l): l is string => !!l && !!l.trim()),
            city: from?.address?.city ?? '',
            stateProvinceCode: from?.address?.region ?? '',
            postalCode: from?.address?.postalCode ?? '',
            countryCode: from?.address?.country || 'US',
        },
    };

    const billingType = shipment.billing?.type ?? 'shipper';
    const billing = billingType === 'shipper'
        ? { type: 'shipper' as const }
        : billingType === 'receiver'
            ? {
                type: 'receiver' as const,
                accountNumber: trim(shipment.billing?.accountNumber),
                // Receiver is the consignee, so bill against the ship-to's postal/country.
                postalCode: trim(to?.address?.postalCode),
                countryCode: trim(to?.address?.country) || 'US',
              }
            : {
                type: 'thirdParty' as const,
                accountNumber: trim(shipment.billing?.accountNumber),
                postalCode: trim(shipment.billing?.postalCode) || undefined,
                countryCode: trim(shipment.billing?.countryCode) || '',
              };

    return {
        shipper,
        shipTo: {
            name: to?.company ?? '',
            // Recipient contact prints on the label as AttentionName + Phone (matches buildInput()).
            attentionName: to?.contactName || undefined,
            phone: to?.phone || undefined,
            email: to?.email || undefined,
            address: {
                addressLines: [to?.address?.line1, to?.address?.line2].filter((l): l is string => !!l && !!l.trim()),
                city: to?.address?.city ?? '',
                stateProvinceCode: to?.address?.region ?? '',
                postalCode: to?.address?.postalCode ?? '',
                countryCode: to?.address?.country || '',
            },
        },
        serviceCode: shipment.serviceCode,
        serviceDescription: shipment.serviceDescription,
        residential: to?.residential,
        packages: (shipment.packages ?? []).map((p) => ({
            weightLbs: trim(p.weightLbs),
            lengthIn: trim(p.lengthIn) || undefined,
            widthIn: trim(p.widthIn) || undefined,
            heightIn: trim(p.heightIn) || undefined,
            declaredValue: trim(p.packageValue) || undefined,
            referenceNumbers: [trim(p.reference1), trim(p.reference2)].filter((r) => r.length > 0),
        })),
        description: `UPS Shipment ${shipment.id.slice(-4)}`,
        referenceNumbers: shipment.referenceNumbers,
        billing,
        notifyRecipients: (shipment.notifyRecipients ?? []).map((r) => ({
            email: r.email, onShip: r.onShip, onException: r.onException, onDelivery: r.onDelivery,
        })),
    };
}

export interface UpsPartyJson {
    Name: string;
    Address: {
        AddressLine: string[];
        City: string;
        StateProvinceCode: string;
        PostalCode: string;
        CountryCode: string;
    };
    AttentionName?: string;
    Phone?: { Number: string };
    EMailAddress?: string;
    ShipperNumber?: string;
}

export function partyToUps(party: UpsParty): UpsPartyJson {
    const json: UpsPartyJson = {
        Name: party.name,
        Address: {
            AddressLine: party.address.addressLines.filter(Boolean),
            City: party.address.city,
            StateProvinceCode: party.address.stateProvinceCode,
            PostalCode: party.address.postalCode,
            CountryCode: party.address.countryCode || 'US',
        },
    };
    if (party.attentionName) json.AttentionName = party.attentionName;
    if (party.phone) json.Phone = { Number: formatUpsPhone(party.phone) };
    if (party.email) json.EMailAddress = party.email;
    if (party.shipperNumber) json.ShipperNumber = party.shipperNumber;
    return json;
}

/**
 * Format a phone for the UPS label. UPS prints Phone.Number verbatim, so we send a US 10-digit
 * number as `(###) ###-####` (14 chars, within UPS's 15-char limit) rather than a raw digit string.
 * Anything that isn't a clean 10/11-digit US number is passed through trimmed (international, extensions).
 */
function formatUpsPhone(raw: string): string {
    const digits = (raw || '').replace(/\D/g, '');
    const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (ten.length === 10) return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
    return (raw || '').trim();
}
